/**
 * Which faces a stroke is allowed to touch.
 *
 * A classic selection tool: pick some faces, and until you clear it, painting
 * happens there and nowhere else. **A tool mode, not a layer property.** The
 * schema already carries a `faces` field on a layer, and it stays unused — the
 * two are different things, and picking the wrong one shows up in the places
 * that are hardest to change later:
 *
 *   a layer property   is permanent and travels in the file, so it belongs to
 *                      the artwork. Undo has to restore it, export has to honour
 *                      it, and a project opened tomorrow still has it.
 *   a tool mode        is where you are working right now. It survives no
 *                      reload, records no history, changes no file, and can be
 *                      dropped the moment somebody stops thinking about it.
 *
 * The second is what a selection is everywhere else, and expectations about a
 * selection are strong: people expect Escape to clear it and nothing to remember
 * it. So nothing here is serialisable and nothing here reaches the format.
 *
 * ## Empty means everything
 *
 * The convention every editor uses, and it matters more here than usual. Face
 * boundaries are invisible on the body — the whole app exists to say so — so a
 * selection nobody made must never quietly stop paint. Empty is not "nothing may
 * be painted", it is "no restriction", which is also the state to fall back to
 * whenever anything is uncertain.
 *
 * ## Immutable
 *
 * Every operation returns a new selection. Selections are at most eighteen
 * entries, so copying is free, and it means the UI can hold one and compare by
 * identity to know whether to redraw rather than diffing sets on every frame.
 */

import type { Rect } from "./format";
import { REGION_IDS, type RegionId } from "./format";
import { GUTTER_PX, REGIONS } from "./regions";

/**
 * The three things a person points at.
 *
 * Not the same as `PartGroup` in parts.ts, deliberately. That answers "which
 * mesh parts does a garment cover" and knows about arms and legs separately;
 * this answers "which strip of the template is that", and a strip is a limb
 * whether it is holding a sleeve or a trouser leg. The template has one layout
 * and reuses it, so a selection has three groups and the label on screen is the
 * garment's business.
 */
export type FaceGroup = "torso" | "rightLimb" | "leftLimb";

export const FACE_GROUPS: readonly FaceGroup[] = ["torso", "rightLimb", "leftLimb"];

/** Which strip a face belongs to. Read off the id, which is already grouped. */
export function groupOf(id: RegionId): FaceGroup {
  return id.split(".")[0] as FaceGroup;
}

/** Every face of one strip, in template order. */
export function facesOf(group: FaceGroup): RegionId[] {
  return REGION_IDS.filter((id) => groupOf(id) === group);
}

/**
 * A selection. Empty means no restriction — see the note above.
 *
 * A plain readonly set rather than a class, so it is trivially comparable,
 * trivially logged, and impossible to accidentally give behaviour that belongs
 * somewhere else.
 */
export type Selection = ReadonlySet<RegionId>;

export const EMPTY: Selection = new Set<RegionId>();

/** Whether a stroke may touch this face. */
export function allows(selection: Selection, id: RegionId): boolean {
  return selection.size === 0 || selection.has(id);
}

/**
 * Whether a stroke may touch this texel.
 *
 * A selected face is allowed **with its gutter**, matching `paintableMask` in
 * paint.ts, which is what this ends up intersected with. That matters for one
 * reason: a stroke on a selected face should have exactly the shape it would
 * have with no selection at all. Clipping to the bare rect instead would shave
 * two pixels off every edge of the selected face, so turning a selection on
 * would visibly change strokes it was supposed to leave alone.
 *
 * The gutter between a selected and an unselected face is therefore allowed
 * from one side. That costs nothing: the gutter is never sampled by the body,
 * and export refills it by dilating from whichever region reaches it.
 */
export function allowsAt(selection: Selection, x: number, y: number): boolean {
  if (selection.size === 0) return true;
  for (const id of selection) {
    const r = REGIONS[id];
    if (
      x >= r.x - GUTTER_PX &&
      x < r.x + r.w + GUTTER_PX &&
      y >= r.y - GUTTER_PX &&
      y < r.y + r.h + GUTTER_PX
    ) {
      return true;
    }
  }
  return false;
}

/** Add or remove one face. */
export function toggleFace(selection: Selection, id: RegionId): Selection {
  const next = new Set(selection);
  if (!next.delete(id)) next.add(id);
  return next;
}

/**
 * Add or remove a whole strip.
 *
 * Removes only when the strip is already wholly selected. Otherwise a click on
 * a half-selected limb would clear the faces somebody had just picked, which is
 * the opposite of what reaching for "the whole arm" means.
 */
export function toggleGroup(selection: Selection, group: FaceGroup): Selection {
  const faces = facesOf(group);
  const next = new Set(selection);
  if (faces.every((id) => next.has(id))) {
    for (const id of faces) next.delete(id);
  } else {
    for (const id of faces) next.add(id);
  }
  return next;
}

/**
 * Every face, as an explicit set.
 *
 * Not the same VALUE as `EMPTY`, though it means the same thing to `allows` —
 * and that difference is what makes Invert behave. Selecting all and then
 * inverting has to land on "no restriction", which is `EMPTY`; if Select all
 * produced `EMPTY` in the first place, inverting it would select everything and
 * the pair of buttons would do nothing between them.
 *
 * This is also how Photoshop behaves: select all, invert, and you are back to
 * painting anywhere.
 */
export const ALL: Selection = new Set<RegionId>(REGION_IDS);

/**
 * Swap what is selected for what is not.
 *
 * `invert(EMPTY)` is every face, because empty already means every face and the
 * opposite of that is nothing to paint on... which is not a useful state, so it
 * is read the other way: an untouched selection inverts to an explicit full set.
 */
export function invert(selection: Selection): Selection {
  return new Set(REGION_IDS.filter((id) => !selection.has(id)));
}

/**
 * Add faces without removing any.
 *
 * What a DRAG does, as opposed to a tap. A drag that toggled would flicker
 * faces on and off as a finger wobbled back across a boundary it had already
 * crossed — so dragging only ever adds, and removing one is a deliberate tap.
 */
export function add(selection: Selection, ids: readonly RegionId[]): Selection {
  const next = new Set(selection);
  for (const id of ids) next.add(id);
  return next;
}

/** Replace the selection with exactly these faces. */
export function selectOnly(ids: readonly RegionId[]): Selection {
  return new Set(ids);
}

/** Whether every face of a strip is in the selection. */
export function hasWholeGroup(selection: Selection, group: FaceGroup): boolean {
  return facesOf(group).every((id) => selection.has(id));
}

/**
 * The rects a stroke may land in, for clipping and for dimming what it may not.
 *
 * Empty selection returns every region, so a caller can build one clip path
 * without branching on whether a selection exists.
 */
export function selectedRects(selection: Selection): Rect[] {
  const ids = selection.size === 0 ? REGION_IDS : REGION_IDS.filter((id) => selection.has(id));
  return ids.map((id) => REGIONS[id]);
}

/**
 * The rects paint may occupy, gutter included.
 *
 * The same geometry `allowsAt` tests against, handed out as rectangles so a
 * caller can build a clip path instead of asking per texel. Two things use it —
 * the brush's mask and a layer restricted to some faces — and they must agree,
 * because a stroke allowed to land somewhere the composite then clips away is
 * paint that exists in the file and cannot be seen.
 *
 * **The gutter is included, and that is the whole subtlety.** Roblox leaves two
 * unpainted pixels between adjacent faces and its own docs say to paint into
 * them, so clipping to the bare rect would shave a hairline off every edge of
 * every restricted layer — the artifact the bleed exists to prevent, reappearing
 * only for people who used this feature.
 *
 * An empty selection returns every face, so a caller can pass one straight
 * through without branching on whether a restriction exists.
 */
export function paintRects(selection: Selection): Rect[] {
  return selectedRects(selection).map((r) => ({
    x: Math.max(0, r.x - GUTTER_PX),
    y: Math.max(0, r.y - GUTTER_PX),
    w: r.w + GUTTER_PX * 2,
    h: r.h + GUTTER_PX * 2,
  }));
}

/** The faces a stroke may NOT land in — what the UI dims. */
export function excludedFaces(selection: Selection): RegionId[] {
  return selection.size === 0 ? [] : REGION_IDS.filter((id) => !selection.has(id));
}

/**
 * What the selection says, in words, for a chip and for a screen reader.
 *
 * Named by garment because the template reuses one limb strip for both: the
 * same six faces are a sleeve on a shirt and a trouser leg on pants, and
 * "rightLimb" is not something to show anybody.
 *
 * Left and right are the CHARACTER's, matching the mirror commands, so the two
 * features cannot disagree about which arm is which.
 */
export function describe(selection: Selection, garment: "shirt" | "pants" = "shirt"): string {
  // Both spellings of "no restriction" read the same, because they mean the
  // same: nothing chosen, and everything chosen, both let a stroke anywhere.
  if (selection.size === 0 || selection.size === REGION_IDS.length) return "All faces";
  const limb = limbNoun(garment);
  const whole = FACE_GROUPS.filter((g) => hasWholeGroup(selection, g));
  const accounted = whole.flatMap((g) => facesOf(g)).length;
  const label = (g: FaceGroup) =>
    g === "torso" ? "Torso" : g === "leftLimb" ? `Left ${limb}` : `Right ${limb}`;

  if (whole.length > 0 && accounted === selection.size) {
    return whole.map(label).join(" + ");
  }
  return `${selection.size} face${selection.size === 1 ? "" : "s"}`;
}

/**
 * What a limb is called on this garment.
 *
 * The template reuses one limb strip for both, so the same six faces are an arm
 * on a shirt and a leg on pants. Exported because every surface that names a
 * region needs it and they must not each invent their own word — the editor
 * saying "arm" while the homepage said "sleeve" was exactly that.
 */
export function limbNoun(garment: "shirt" | "pants"): string {
  return garment === "pants" ? "leg" : "arm";
}

/**
 * What to call one face, on the body rather than in the id.
 *
 * The ids are `torso.left`, `rightLimb.left` and so on, and for a limb that
 * second word is the trap this whole app exists around. `rightLimb.left` is not
 * "the left of the right arm" in any sense a person means — it is the side
 * pressed against the torso, measured at 0.0026 studs from `torso.right` and
 * hidden by it. Printing "Left" on that cell would be as misleading as putting
 * it in a checklist, which is the reason a checklist was not the answer.
 *
 * So limbs get **Inner** and **Outer**, which is what they are. The torso keeps
 * Left and Right, because there they mean what they say — the character's own
 * sides, matching `describe` and the mirror commands.
 *
 * Up and down become Top and Bottom for the same reason: "up" is a direction and
 * a cap is a place.
 */
export function faceLabel(id: RegionId): string {
  const [group, face] = id.split(".") as [FaceGroup, string];
  if (face === "up") return "Top";
  if (face === "down") return "Bottom";
  if (group === "torso") return face === "left" ? "Left" : face === "right" ? "Right" : cap(face);
  // A limb's inner side is the one against the torso: for the right limb that
  // is its `left`, and the left limb mirrors it.
  const inner = group === "rightLimb" ? "left" : "right";
  if (face === inner) return "Inner";
  if (face === "left" || face === "right") return "Outer";
  return cap(face);
}

function cap(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}
